import React, { useEffect, useState } from 'react';

import { Form, Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import CustomFileUpload from '@components/common/CustomFileUpload/CustomFileUpload';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Text from '@components/common/Text/Text';
import Choice from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/Revives/Choice/Choice';
import { inviteType } from '@propTypes/inviteType';
import PotService from '@services//domain/Lpc/PotService';
import ObjectService from '@services/utils/ObjectService';
import { checkValidEmail } from '@services/utils/StringService';

import css from './Invitation.module.scss';

type InvitationFormProps = {
    onCloseModal: () => void;
    setActiveTab: (number) => void;
    onSetEmailList: (any) => void;
    emailList: string[];
    potRef: string;
};

const Invitation = ({ onCloseModal, setActiveTab, onSetEmailList, emailList, potRef }: InvitationFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();

    const [tags, setTags] = useState(emailList);
    const [inputTerm, setInputTerm] = useState('');

    const [invites, setInvites] = useState<inviteType[]>([]);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [displayedInvites, setDisplayedInvites] = useState<inviteType[]>([]);
    const [initialDropDownPosition, setInitialDropDownPosition] = useState<number>(0);

    useEffect(() => {
        setInitialDropDownPosition(document.getElementById('emailInput').getBoundingClientRect().top);
        PotService.getInvites(potRef).then((res: inviteType[]) => {
            setInvites(processInvites(res));
        });
    }, []);

    useEffect(() => {
        if (showSuggestions && document.getElementById('suggestions')) {
            const offsets = document.getElementById('emailInput').getBoundingClientRect();
            document.getElementById('suggestions').style.top = offsets.top - initialDropDownPosition - 70 + 'px';
        }
    }, [showSuggestions, tags]);

    const processInvites = (res) => {
        if (ObjectService.hasContent(res)) {
            return res.map((r, index) => {
                r.index = index;
                return r;
            });
        }
        return [];
    };

    const onSearch = (value: string) => {
        if (ObjectService.hasContent(invites)) {
            if (!value || !value.trim()) {
                setDisplayedInvites(invites);
            } else {
                setDisplayedInvites(
                    invites.filter((e) => {
                        if (e.email.includes(value)) {
                            return e;
                        }
                    }),
                );
            }
            setShowSuggestions(true);
        }
    };

    const onCancel = () => {
        onCloseModal();
    };

    const onSubmit = () => {
        if (tags.length > 0) {
            onSetEmailList(tags);
        }
    };

    const getInitialValues = () => {
        return {
            email: '',
        };
    };

    const onAddTag = (tag) => {
        const newTags = [...tags, tag];
        setTags(newTags);
    };

    const setSelectedEmail = (index: number, selected: boolean) => {
        const invitesTemp = invites.map((e) => {
            if (e.index === index) {
                e.selected = selected;
            }
            return e;
        });
        setInvites(invitesTemp);
    };

    const onDeleteTag = (tag) => {
        const deleteTag = tags.filter((item) => item !== tag);
        if (ObjectService.hasContent(invites)) {
            onDeleteFromInvites(tag);
        }
        setTags(deleteTag);
    };

    const onDeleteFromInvites = (tag) => {
        const updateInvites = invites.map((item) => {
            if (item.email === tag) {
                item.selected = false;
            }
            return item;
        });
        setInvites(updateInvites);
    };

    const onDeleteLastTag = (lastTag) => {
        const newTags = [...tags];
        newTags.splice(lastTag, 1);
        if (ObjectService.hasContent(invites)) {
            onDeleteFromInvites(tags[lastTag]);
        }
        setTags(newTags);
    };

    const renderTags = () => {
        return tags.map((tag, index) => (
            <div key={index} className={css.input__container__tags__item}>
                {tag}
                <img onClick={() => onDeleteTag(tag)} src="/front-static/icons/navigation/close.svg" alt="delete" />
            </div>
        ));
    };

    const onKeyDown = (e) => {
        if ((e.key === 'Enter' || e.keyCode === 32) && inputTerm) {
            if (!checkValidEmail(inputTerm)) {
                return;
            }
            if (tags.find((tag) => tag.toLowerCase() === inputTerm.toLowerCase())) {
                return;
            }
            onAddTag(inputTerm);
            setInputTerm('');
        } else if (e.key === 'Backspace' && !inputTerm) {
            onDeleteLastTag(tags.length - 1);
        }
    };

    const handleBlur = () => {
        setTimeout(() => {
            setShowSuggestions(false);
        }, 500);
        if (!checkValidEmail(inputTerm)) {
            return;
        }
        if (tags.find((tag) => tag.toLowerCase() === inputTerm.toLowerCase())) {
            return;
        }
        onAddTag(inputTerm);
        setInputTerm('');
    };

    const toggleSelectInvite = (invite) => {
        if (tags.find((tag) => tag.toLowerCase() === invite.email.toLowerCase())) {
            onDeleteTag(invite.email);
            setSelectedEmail(invite.index, false);
        } else {
            onAddTag(invite.email);
            setSelectedEmail(invite.index, true);
        }
        setInputTerm('');
    };

    const onUploadXls = async (file) => {
        // to Base 64
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => {
            // to form data
            const bodyFormData = new FormData();
            // @ts-ignore
            bodyFormData.append('stream', reader.result);
            bodyFormData.append('potRef', potRef);

            PotService.getEmailFromXls(bodyFormData)
                .then((res) => setTags(res.data))
                .catch(() => {
                    addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                        appearance: 'error',
                        autoDismiss: true,
                    });
                });
        };
    };

    return (
        <>
            <Formik initialValues={getInitialValues()} onSubmit={() => onSubmit()}>
                {() => (
                    <Form>
                        <>
                            <Text variant={'caption_01'} color={'txt-primary'}>
                                {t('invitation.add-email-label')}
                            </Text>
                            <div className={css.invite__wrapper}>
                                <CustomFileUpload
                                    inputName="xls"
                                    onFileUpload={(file) => onUploadXls(file)}
                                    fileType={'xlsx'}
                                >
                                    <Text customClass={css.import__cta} variant={'body_02'} color={'txt-secondary'}>
                                        {t('invitation.import-email-cta')}
                                        <img
                                            className={css.import__cta__icon}
                                            src="/front-static/icons/logo/excel.svg"
                                            alt="excel"
                                        />
                                    </Text>
                                </CustomFileUpload>
                                <div className={css.input__container}>
                                    <div className={css.input__container__tags}>
                                        {renderTags()}
                                        <input
                                            placeholder={t('invitation.add-email-placeholder')}
                                            className={css.input__container__tags__input}
                                            name={'email'}
                                            id={'emailInput'}
                                            type="text"
                                            autoComplete={'off'}
                                            onChange={(e) => {
                                                setInputTerm(e.target.value);
                                                onSearch(e.target.value);
                                            }}
                                            onKeyDown={onKeyDown}
                                            onBlur={handleBlur}
                                            value={inputTerm}
                                        />
                                    </div>
                                </div>
                                {showSuggestions && ObjectService.hasContent(displayedInvites) && (
                                    <div className={css.choices} id={'suggestions'}>
                                        {displayedInvites.map((element, i) => (
                                            <Choice
                                                invited={true}
                                                onClick={() => {
                                                    toggleSelectInvite(element);
                                                }}
                                                email={displayedInvites[i].email}
                                                selected={displayedInvites[i].selected}
                                                key={i}
                                                customClass={css.choices__one}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={css.counter}>
                                <Text customClass={css.counter__number} variant={'caption_02'} color={'brand-primary'}>
                                    {tags.length}
                                </Text>
                                <Text variant={'caption_02'} color={'txt-secondary'}>
                                    {tags.length > 1
                                        ? t('invitation.invited-person_plural')
                                        : t('invitation.invited-person')}
                                </Text>
                            </div>
                            {/* SUBMIT */}
                            <div className={css.form__submit}>
                                <Button
                                    type={'button'}
                                    customClass={css.form__submit__cta}
                                    variant={'secondary'}
                                    onClick={() => onCancel()}
                                >
                                    {t('invitation.cancel')}
                                </Button>
                                <Button
                                    type={'button'}
                                    margin={'0 0 0 8px'}
                                    variant={'primary'}
                                    customClass={css.form__submit__cta}
                                    isDisabled={tags.length < 1}
                                    onClick={() => {
                                        setActiveTab(1);
                                        onSubmit();
                                    }}
                                >
                                    {t('invitation.next')}
                                </Button>
                            </div>
                        </>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default Invitation;
